/**
 * M910 — Suppress the lead-form popup when the user navigated explicitly
 * via a CTA.
 *
 * The exit-intent / scroll popup is designed to assist users who DISCOVER
 * the form by scrolling. When the user clicks an intentional CTA
 * ("Înscrie-te", "Cere broșura", "Vezi ce vei învăța", etc.) they're
 * already committed to the form — surfacing the popup right after the
 * scroll completes feels redundant and intrusive.
 *
 * Mechanism:
 *   1. CTA `onClick` calls `scrollToSection(id)` from here.
 *   2. This sets `sessionStorage["suppress_form_popup"] = "1"` BEFORE
 *      scrolling. The flag is one-shot: `ExitIntent.shouldSuppress`
 *      reads it, clears it, and skips the popup ONCE.
 *   3. On the next regular scroll-driven session, the flag is gone and
 *      the popup behaves exactly as before.
 *
 * Centralisation: every place that scrolls to a homepage section calls
 * this helper, so adding a new CTA in the future doesn't require touching
 * the suppress logic.
 */

export const SUPPRESS_FORM_POPUP_KEY = "suppress_form_popup";

/**
 * Mark the next popup-eligible moment as "suppressed". Safe in SSR/storage-
 * disabled contexts: silently no-ops when sessionStorage is unavailable.
 */
export function suppressFormPopupOnce() {
  try {
    sessionStorage.setItem(SUPPRESS_FORM_POPUP_KEY, "1");
  } catch {
    /* ignore — storage may be denied (private mode, ITP, etc.) */
  }
}

/**
 * Read-and-clear the suppress flag. Returns `true` if the popup must be
 * suppressed THIS time, `false` otherwise. One-shot — calling this twice
 * returns `false` on the second call.
 */
export function consumeSuppressFormPopup() {
  try {
    const v = sessionStorage.getItem(SUPPRESS_FORM_POPUP_KEY);
    if (v === "1") {
      sessionStorage.removeItem(SUPPRESS_FORM_POPUP_KEY);
      return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}

/**
 * Smoothly scrolls the page to an element by `id`. If the target sits in
 * the second half of the page (i.e. it would naturally trigger the popup
 * scroll-threshold of 55–65%), the suppress flag is set first.
 *
 * Pass `{ suppressPopup: true }` to force-suppress regardless of position
 * (used by CTAs that go to `lead-form` directly, since that section is
 * always past the threshold).
 */
export function scrollToSection(id, { suppressPopup = true } = {}) {
  if (suppressPopup) suppressFormPopupOnce();
  const el = typeof document !== "undefined" ? document.getElementById(id) : null;
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}
